import { Injectable } from '@angular/core';
import { Storage } from '@ionic/storage';
import { BehaviorSubject, Subject, Observable } from 'rxjs';
import { StatoCompletoEvento, Evento } from 'src/app/common/models/Evento';

@Injectable({
  providedIn: 'root'
})
export class EventiDaSincronizzareService {
  private readonly STORAGE_KEY_EVENTI_DA_SINCRONIZZARE_PREFISSO = '__eventiDaSincronizzare';

  /**
   * Per ogni intervento, identificato dal proprio id, mantiene un Subject che propaga
   * ai sottoscriventi l'elenco degli id dei suoi eventi disallineati
   */
  private mapIdEventiDisallineatiSubject: Map<number, Subject<number[]>> = new Map<number, Subject<number[]>>();

  constructor(private storage: Storage) { }

  async getElencoIdEventiDisallineatiObservable(idIntervento: number): Promise<Observable<number[]>> {
    const eventiInterventoObs = this.getEventiInterventoSubj(idIntervento);
    await this.aggiornaObservableIdEventiDisallineati(idIntervento); // N eventi ciascuno scatena la lettura dal Db
    return eventiInterventoObs.asObservable();
  }

  private getEventiInterventoSubj(idIntervento: number): Subject<number[]> {
    let subj = this.mapIdEventiDisallineatiSubject.get(idIntervento);
    if (!subj) { // Lo inizializzo a vuoto
      subj = new BehaviorSubject<number[]>([]);
      this.mapIdEventiDisallineatiSubject.set(idIntervento, subj);
    }
    return subj;
  }

  private getStorageKey(idIntervento: number): string {
    return `${this.STORAGE_KEY_EVENTI_DA_SINCRONIZZARE_PREFISSO}_${idIntervento}`;
  }

  /**
   * Aggiunge al Db di tutti gli eventi dell'intervento 
   * l'oggetto passato.
   * Ritorna quanto ottenuto, dopo averlo salvato su Db
   * @param statoEventoDaRimuovere 
   * @returns tutti gli eventi da sincronizzare presenti per lo stesso intervento
   * cui apparteneva l'oggetto da aggiungere
   */
  async salvaEventoDaSincronizzare(statoEventoDaSalvare: StatoCompletoEvento): Promise<StatoCompletoEvento[]> {
    let eventiFiltrati = await this.rimuoviEventoDaSincronizzare(statoEventoDaSalvare);
  
    /**
     * Se sto inserendo un evento presenteSoloLocalmente, allora ho la necessità di usare un idEvento fittizio
     * (idEvento ultimo evento salvato incrementato di 1) che poi verrà sovrascritto dall'idEvento corretto una 
     * volta sincronizzato.
     * Inoltre è necessario tenere traccia dell'idEvento fittizio per effettuare, dopo l'avvenuta sincronizzazione,
     * l'eliminazione dell'evento dalla lista degli eventi da sincronizzare. 
     */
    if(statoEventoDaSalvare.datiEvento.idEvento == 0) {
      // Ottieni l'ultimo ID evento
      let ultimoIDEvento = 0;
      if(eventiFiltrati.length > 0) {
          ultimoIDEvento = eventiFiltrati[0].datiEvento.idEvento;
      }
    
      // Incrementa l'ID evento
      statoEventoDaSalvare.datiEvento.idEvento = ultimoIDEvento + 1;
      statoEventoDaSalvare.datiEvento.idEventoLocale = ultimoIDEvento + 1;
    }
    eventiFiltrati.unshift(statoEventoDaSalvare);
    await this.storage.set(this.getStorageKey(statoEventoDaSalvare.datiEvento.idIntervento), eventiFiltrati);
  
    return eventiFiltrati;
  }

  /**
   * Rimuove dal Db tutti gli eventi dell'intervento aventi come id quello dell'oggetto
   * passato.
   * Ritorna quanto rimasto, dopo averlo salvato su Db
   * @param statoEventoDaRimuovere 
   * @returns tutti gli eventi da sincronizzare rimasti per lo stesso intervento
   * cui apparteneva l'oggetto da rimuovere
   */
  async rimuoviEventoDaSincronizzare(statoEventoDaRimuovere: StatoCompletoEvento): Promise<StatoCompletoEvento[]> {
    const { idIntervento, idEvento, idEventoLocale } = statoEventoDaRimuovere.datiEvento;
    const statoPrecedenteEventi = await this.getEventiDaSincronizzare(idIntervento);
    // Rimuovo l'eventuale stato precedente del singolo evento
    const eventiFiltrati = statoPrecedenteEventi.filter(statoEvento => statoEvento.isPresenteSoloLocalmente ? statoEvento.datiEvento.idEvento !== idEventoLocale : statoEvento.datiEvento.idEvento !== idEvento);
    await this.storage.set(this.getStorageKey(idIntervento), eventiFiltrati);
    return eventiFiltrati;
  }

  /**
   * Cancella TUTTI gli eventi disallineati associati all'intervento fornito
   * (nuovi eventi o modifiche non pubblicate) 
   * @param idIntervento 
   */
  async rimuoviEventiDaSincronizzare(idIntervento: number) {
    const key = this.getStorageKey(idIntervento);
    await this.storage.remove(key);
  }

  /**
   * Ritorna true se per l'evento fornito esistono su Db
   * modifiche non sincronizzate (e quindi vanno mostrati
   * tali dati o un indicatore)
   * @param evento 
   */
  async isModificheDaSincronizzarePresenti(evento: Evento): Promise<boolean> {
    const { idIntervento, idEvento } = evento;
    return (!! await this.getEventoDaSincronizzare(idIntervento, idEvento));
  }

  async isEventoPresenteSoloOffline(idIntervento: number, idEvento: number): Promise<boolean> {
    const eventiSoloOffline = await this.getEventiPresentiSoloOffline(idIntervento);
    return eventiSoloOffline
      .map(evento => evento.datiEvento.idEvento)
      .includes(idEvento);
  }

  async getEventiPresentiSoloOffline(idIntervento: number): Promise<StatoCompletoEvento[]> {
    const elencoEventiDisallineati = await this.getEventiDaSincronizzare(idIntervento);
    return elencoEventiDisallineati
      .filter(statoEvento => statoEvento.isPresenteSoloLocalmente === true);
  }

  /**
   * Ritorna l'evento da sincronizzare indicato. L'evento sottoscrivente dovrebbe sovrascrivere
   * i suoi dati con quelli ritornati perché le modifiche locali, per l'utente, prevalgono
   * @param idIntervento 
   * @param idEventoRicercato 
   */
  async getEventoDaSincronizzare(idIntervento: number, idEventoRicercato: number): Promise<StatoCompletoEvento> {
    const eventi = await this.getEventiDaSincronizzare(idIntervento);
    const eventoCompatibile = eventi
      .filter(statoEvento => statoEvento.datiEvento.idEvento === idEventoRicercato)
    [0]; // Sempre uno
    return eventoCompatibile;
  }

  /**
   * Dopo un'operazione di aggiornamento ritrasmetto l'elenco
   * degli id di tutti gli eventi aperti ai sottoscriventi
   * @returns elenco degli id degli eventi aperti attualmente presenti
   * su db
   */
  private async aggiornaObservableIdEventiDisallineati(idIntervento: number): Promise<number[]> {
    const eventiDaSincronizzare = await this.getEventiDaSincronizzare(idIntervento);
    const elencoId = eventiDaSincronizzare.map(statoEvento => statoEvento.datiEvento.idEvento);
    const eventiInterventoObs = this.getEventiInterventoSubj(idIntervento);
    eventiInterventoObs.next(elencoId);
    return elencoId;
  }

  /**
   * Ritorna tutti gli eventi che risultano da sincronizzare per l'intervento
   * fornito o lista vuota
   * @param idIntervento 
   */
  async getEventiDaSincronizzare(idIntervento: number): Promise<StatoCompletoEvento[]> {
    const storageKey = this.getStorageKey(idIntervento);
    const eventiSuStorage = await this.storage.get(storageKey);
    const eventiDaSincronizzare = !!eventiSuStorage ? eventiSuStorage : [];
    return eventiDaSincronizzare;
  }
}
